import React, { useEffect, useState, useMemo } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { api } from '@/api/client';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { FilterBar } from '@/components/ui/FilterBar';
import { Printer, Plus, Edit, Trash } from 'lucide-react';
import { printDocument, buildRepairContent } from '@/utils/printDocs';
import { RepairForm } from '@/components/forms/RepairForm';
import { useAuth } from '@/context/AuthContext';
import { REPAIR_STATUSES } from '@/utils/helpers';
import { exportToCSV, CSV_COLUMNS } from '@/utils/exportUtils';

export const Repairs = () => {
  const [repairs, setRepairs] = useState([]);
  const [customers, setCustomers] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editingRepair, setEditingRepair] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const { isAdmin, isSuperAdmin } = useAuth();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [repRes, custRes] = await Promise.all([
        api.getRepairs(),
        api.getCustomers()
      ]);
      const sorted = repRes.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setRepairs(sorted);
      const cmap = {};
      custRes.data.forEach(c => { cmap[c.id] = c; });
      setCustomers(cmap);
    } catch (error) {
      console.error('Error loading repairs:', error);
    }
  };

  const handlePrint = (repair) => {
    const content = buildRepairContent({
      repair,
      customer: customers[repair.customer_id]
    });
    printDocument({ title: `Repair-${repair.id.slice(0, 8)}`, content });
  };

  const handleEdit = (repair) => {
    setEditingRepair(repair);
    setShowForm(true);
  };

  const handleDelete = async (repair) => {
    if (!window.confirm('Delete this repair?')) return;
    try {
      await api.deleteRepair(repair.id);
      loadData();
    } catch (error) { alert('Failed to delete'); }
  };

  const handleNew = () => {
    setEditingRepair(null);
    setShowForm(true);
  };

  const filteredRepairs = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return repairs.filter(r => {
      if (statusFilter && r.status !== statusFilter) return false;
      if (!q) return true;
      const customer = customers[r.customer_id];
      return r.device?.toLowerCase().includes(q) ||
        r.issue?.toLowerCase().includes(q) ||
        customer?.name?.toLowerCase().includes(q);
    });
  }, [repairs, searchTerm, statusFilter, customers]);

  return (
    <div className="space-y-6" data-testid="repairs-page">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Repairs</h1>
        <Button onClick={handleNew} data-testid="new-repair-button">
          <Plus size={20} className="mr-2" />
          New Repair
        </Button>
      </div>

      <Card>
        <FilterBar
          search={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search by device, issue, or customer..."
          filters={[{
            key: 'status',
            label: 'All Statuses',
            value: statusFilter,
            options: REPAIR_STATUSES,
            onChange: setStatusFilter
          }]}
          onClear={() => { setSearchTerm(''); setStatusFilter(''); }}
          onExport={() => exportToCSV(filteredRepairs, 'repairs', CSV_COLUMNS.repairs)}
          testId="repairs-filter"
        />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Job ID</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Device / Product</TableHead>
              <TableHead>Serial</TableHead>
              <TableHead>Issue</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Supplier RMA</TableHead>
              <TableHead>Cost</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRepairs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8 text-gray-500">
                  {repairs.length === 0 ? 'No repairs found. Click "New Repair" to create one.' : 'No repairs match the filters.'}
                </TableCell>
              </TableRow>
            ) : (
              filteredRepairs.map((repair) => (
                <TableRow key={repair.id}>
                  <TableCell className="font-mono text-xs font-semibold">{repair.job_id || repair.id.slice(0, 8).toUpperCase()}</TableCell>
                  <TableCell>{customers[repair.customer_id]?.name || '-'}</TableCell>
                  <TableCell className="font-medium">{repair.device}</TableCell>
                  <TableCell className="font-mono text-xs">{repair.serial_number || <span className="text-gray-400">—</span>}</TableCell>
                  <TableCell className="max-w-xs truncate">{repair.issue}</TableCell>
                  <TableCell><Badge>{repair.status}</Badge></TableCell>
                  <TableCell className="text-xs">
                    {repair.linked_rma_id
                      ? <Badge variant="info">Linked</Badge>
                      : <span className="text-gray-400">—</span>}
                  </TableCell>
                  <TableCell>${repair.cost}</TableCell>
                  <TableCell>{new Date(repair.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(repair)} data-testid={`edit-repair-${repair.id}`}>
                        <Edit size={16} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handlePrint(repair)} data-testid={`print-repair-${repair.id}`}>
                        <Printer size={16} />
                      </Button>
                      {isSuperAdmin() && (
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(repair)} data-testid={`delete-repair-${repair.id}`}>
                          <Trash size={16} className="text-red-600" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <RepairForm isOpen={showForm} onClose={() => setShowForm(false)} repair={editingRepair} onSaved={loadData} />
    </div>
  );
};
